<!--
  - ===========================================================================
  - [StrongGate Limited] [http://www.stronggate.com]
  - Copyright (c) 2026. All rights Reserved.
  - Unauthorized access, use, dissemination, distribution, reproduction of this
  - file, or a portion of, in any form is prohibited and unlawful. If you receive
  - this file, and you are not an authorized company employee, or you believe you
  - have received this file in error, please notify the sender (or originator)
  - and promptly delete this file.
  -
  - Project:
  - File: ztn-header-dropdown.vue
  - Created by: chan.ying
  - Create Date: 2026-07-27
  - Version Number: 1.0
  - Version Description: Header dropdown component with click-outside close behavior
  -
  - =============================================================================
  -->

<template>
  <div class="ztn-header-dropdown" ref="dropdownRef">
    <div
        class="ztn-header-dropdown-trigger"
        :class="{ 'is-active': isOpen }"
        @click.stop="toggle"
    >
      <slot name="trigger"></slot>
    </div>

    <transition name="ztn-header-dropdown-fade">
      <div
          v-show="isOpen"
          class="ztn-header-dropdown-menu"
          :class="[`ztn-header-dropdown-menu--${placement}`]"
          :style="menuStyle"
          @click.stop
      >
        <div class="ztn-header-dropdown-body">
          <slot name="body"></slot>
        </div>
        <slot></slot>
      </div>
    </transition>
  </div>
</template>

<script>
import emitter from "@/utils/mitt/eventBus";

export default {
  name: "ztn-header-dropdown",
  props: {
    width: {
      type: [String, Number],
      default: "240px"
    },
    placement: {
      type: String,
      default: "bottom-center",
      validator: (value) => [
        "bottom-left",
        "bottom-center",
        "bottom-right",
        "top-left",
        "top-center",
        "top-right",
      ].includes(value),
    },
    offset: {
      type: Number,
      default: 8,
    },
  },
  data() {
    return {
      isOpen: false,
      menuPosition: {top: 0, left: 0},
    };
  },
  computed: {
    menuStyle() {
      return {
        width: typeof this.width === "number" ? `${this.width}px` : this.width,
        top: `${this.menuPosition.top}px`,
        left: `${this.menuPosition.left}px`,
      };
    },
  },
  mounted() {
    document.addEventListener("click", this.handleDocumentClick);
    window.addEventListener("resize", this.recalculatePosition);
    window.addEventListener("scroll", this.recalculatePosition, true);
    emitter.on("CLOSE_OTHER_DROPDOWNS", this.closeOtherDropdowns);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleDocumentClick);
    window.removeEventListener("resize", this.recalculatePosition);
    window.removeEventListener("scroll", this.recalculatePosition, true);
    emitter.off("CLOSE_OTHER_DROPDOWNS", this.closeOtherDropdowns);
  },
  methods: {
    closeOtherDropdowns(exceptRef) {
      if (this.$refs.dropdownRef !== exceptRef) {
        this.isOpen = false;
      }
    },
    toggle() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.$emit("open");
        this.$nextTick(() => {
          this.recalculatePosition();
          emitter.emit("CLOSE_OTHER_DROPDOWNS", this.$refs.dropdownRef);
        });
      }
    },
    close() {
      this.isOpen = false;
    },
    handleDocumentClick(e) {
      if (!this.$refs.dropdownRef.contains(e.target)) {
        this.isOpen = false;
      }
    },
    recalculatePosition() {
      if (!this.isOpen || !this.$refs.dropdownRef) return;

      const trigger = this.$refs.dropdownRef.querySelector(
          ".ztn-header-dropdown-trigger"
      );
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft =
          window.pageXOffset || document.documentElement.scrollLeft;

      const isBottom = this.placement.startsWith("bottom");
      const isLeft = this.placement.endsWith("left");
      const isRight = this.placement.endsWith("right");

      let top = isBottom ? rect.bottom + this.offset : rect.top - this.offset;

      const menuWidth = typeof this.width === "number"
          ? this.width
          : parseInt(this.width, 10);

      let left;
      if (isLeft) {
        left = rect.left;
      } else if (isRight) {
        left = rect.right - menuWidth;
      } else {
        left = rect.left + rect.width / 2 - menuWidth / 2;
      }

      const viewportPadding = 8;

      if (left + scrollLeft + menuWidth > window.innerWidth - viewportPadding) {
        left = window.innerWidth - menuWidth - scrollLeft - viewportPadding;
      }
      if (left + scrollLeft < viewportPadding) {
        left = viewportPadding - scrollLeft;
      }

      this.menuPosition = {
        top: top + scrollTop,
        left: left + scrollLeft,
      };
    },
  },
};
</script>

<style scoped>
.ztn-header-dropdown {
  position: relative;
  display: inline-block;
  z-index: 1;
}

.ztn-header-dropdown-trigger {
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: var(--radius-sm);
  position: relative;
  z-index: 2;
}

.ztn-header-dropdown-trigger:hover {
  background-color: var(--body-aux-4);
}

.ztn-header-dropdown-trigger.is-active {
  background-color: var(--body-aux-4);
}

.ztn-header-dropdown-menu {
  position: fixed;
  background: var(--bg-background);
  box-shadow: var(--elevation-md);
  border-radius: var(--radius-md);
  border: 1px solid var(--body-aux-4);
  overflow: hidden;
  z-index: var(--z-dropdown);
  min-width: v-bind(width);
  padding: var(--space-2);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.ztn-header-dropdown-menu > * {
  padding: var(--space-2) var(--space-3);
  cursor: pointer;
  position: relative;
  z-index: 3;
  border-radius: var(--radius-sm);
  transition: all 0.15s ease;
}

.ztn-header-dropdown-menu * :hover {
  background-color: var(--body-aux-4);
}

.ztn-header-dropdown-body {
  padding: 0 2px 2px !important;
  cursor: default;
  background-color: transparent !important;
}

.ztn-header-dropdown-body:hover {
  background-color: transparent !important;
}

.ztn-header-dropdown-body *:hover:not(.ztn-user-avatar) {
  background-color: transparent !important;
}
</style>
